"use client";

import { useModalStore } from "@b3dotfun/sdk/global-account/react";
import { useEffect, useMemo, useState } from "react";
import { IconCheckOutline18, IconCopyOutline18 } from "nucleo-ui-outline-18";
import ChainSelector from "@/app/(v2)/workflows/_components/form-fields/ChainSelector";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLatest } from "@/hooks/useLatest";
import { verifyIncentiveTask } from "@/lib/api/incentive";
import { POLL_TIMEOUT_MS } from "./useIncentivePolling";

// Native token sentinel — AnySpend modal expects this for ETH/MATIC/BNB/etc
// (matches the convention used in InsufficientBalancePreflightModal /
// GasBalanceWarningModal).
const NATIVE_TOKEN_ADDRESS = "0x0000000000000000000000000000000000000000";

// Default to Base in the chain picker — cheapest gas, B3's preferred chain,
// where most users already have funds. User can switch via the picker.
const DEFAULT_CHAIN_ID = 8453;
interface SupportedChain {
  chainId: number;
  name: string;
}
interface Props {
  open: boolean;
  onClose: () => void;
  treasuryAddress: string;
  supportedChains: SupportedChain[];
  onClaimed: () => void;
}
export function TreasuryModal({
  open,
  onClose,
  treasuryAddress,
  supportedChains,
  onClaimed
}: Props) {
  const [copied, setCopied] = useState(false);
  // Initial value picked once at mount so re-renders don't reset the user's
  // chain choice. Falls back to first supported chain if Base isn't in the
  // list (defensive — chain-registry currently includes Base).
  const initialChainId = useMemo(() => supportedChains.some(c => c.chainId === DEFAULT_CHAIN_ID) ? DEFAULT_CHAIN_ID : supportedChains[0]?.chainId ?? DEFAULT_CHAIN_ID,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  const [chainId, setChainId] = useState<number>(initialChainId);
  const allowedChainIds = useMemo(() => supportedChains.map(c => c.chainId), [supportedChains]);
  const setModalContentType = useModalStore(s => s.setB3ModalContentType);
  const setModalOpen = useModalStore(s => s.setB3ModalOpen);
  const onCloseRef = useLatest(onClose);
  const onClaimedRef = useLatest(onClaimed);

  // Fund Treasury polling: 5s fixed — on-chain confirmation latency dominates,
  // so the smart-tier polling used for OAuth tasks would just burn extra calls.
  useEffect(() => {
    if (!open) return;
    const startedAt = Date.now();
    const id = window.setInterval(async () => {
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        window.clearInterval(id);
        return;
      }
      if (document.visibilityState !== "visible") return;
      try {
        const data = await verifyIncentiveTask("fund_treasury");
        if (data.status === "claimed") {
          window.clearInterval(id);
          onClaimedRef.current();
          onCloseRef.current();
        }
      } catch {
        /* silent retry next tick */
      }
    }, 5000);
    return () => window.clearInterval(id);
  }, [open]);
  const handleCopy = () => {
    navigator.clipboard.writeText(treasuryAddress).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }, () => {
      /* clipboard unavailable (non-secure context / permission denied) —
         address is visible in the box for manual copy */
    });
  };
  const handleDepositViaAnySpend = () => {
    // Hand off to the global AnySpend modal (managed by the SDK's modal store
    // — same pattern as InsufficientBalancePreflightModal / GasBalanceWarningModal).
    // We don't pre-set an amount: anySpendDeposit doesn't expose that prop;
    // the user enters the amount in the AnySpend UI. Copy in this modal tells
    // them the $5 minimum.
    const chainName = supportedChains.find(c => c.chainId === chainId)?.name ?? "selected chain";
    setModalContentType({
      type: "anySpendDeposit",
      recipientAddress: treasuryAddress,
      destinationTokenAddress: NATIVE_TOKEN_ADDRESS,
      destinationTokenChainId: chainId,
      actionLabel: `Fund treasury with native token on ${chainName}`,
      onSuccess: async () => {
        // Trigger an immediate verify so the claim lands as soon as the
        // tx confirms; the 5s poll loop above is the fallback for the case
        // where AnySpend resolves before the tx is indexed.
        try {
          await verifyIncentiveTask("fund_treasury");
        } catch {
          /* polling will retry */
        }
      },
      allowDirectTransfer: true,
      showFiatOption: false
    });
    setModalOpen(true);
  };
  return <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Fund Treasury</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-muted-foreground text-sm">
            Send at least <span className="font-semibold">$5</span> of native token (ETH, BNB, MATIC, ...) to your
            Treasury wallet on any supported chain.
          </p>

          <div className="space-y-2 rounded-lg border p-3">
            <p className="text-xs font-medium">Pay with AnySpend (recommended)</p>
            <p className="text-muted-foreground text-xs">
              Swap from any token on any chain — settles into your treasury.
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <ChainSelector label="" value={chainId} onChange={v => setChainId(Number(v))} allowedChainIds={allowedChainIds} showTestnetToggle={false} popoverModal />
              </div>
              <Button onClick={handleDepositViaAnySpend} variant="brand" className="shrink-0">
                Pay with AnySpend
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium">Or send manually</p>
            <div className="bg-muted flex items-center gap-2 break-all rounded p-3 font-mono text-sm">
              <span className="flex-1">{treasuryAddress}</span>
              <Button iconOnly variant="ghost" onClick={handleCopy} aria-label="Copy address">
                {copied ? <IconCheckOutline18 className="h-4 w-4 text-green-600" /> : <IconCopyOutline18 className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-muted-foreground text-xs">
              Supported chains: {supportedChains.map(c => c.name).join(", ")}
            </p>
          </div>

          <p className="text-muted-foreground text-xs italic">Polling every 5s for confirmation…</p>
        </div>
      </DialogContent>
    </Dialog>;
}